/** @format */

import { Chain, Environment } from '@shared/data-access/interfaces';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, makeStateKey, StateKey, TransferState } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
	Scene7MediaSetItemMin,
	Scene7MediaSetResponse
} from './interfaces/scene7-media-set-response.interface';
import { catchError, map, retry } from 'rxjs/operators';

interface RequestUrlParams {
	cdnName?: string;
	eCode: string;
	mediaSetSuffix: string;
	swatchColor: string;
	swatchColorPrefix: string;
}

interface HttpOptions {
	params: HttpParams;
	responseType: 'text';
}

@Injectable({
	providedIn: 'root'
})
export class Scene7Dao {
	readonly MEDIA_SET_PARSE_ERROR_PREFIX = 'SCENE7_RESPONSE_PARSE_ERROR:';
	readonly MEDIA_SET_STATE_KEY_PREFIX = 'media-set';

	constructor(
		private chain: Chain,
		private environment: Environment,
		private http: HttpClient,
		private state: TransferState
	) {}

	static handleError(error: HttpErrorResponse | any) {
		// return an ErrorObservable
		return throwError(error);
	}

	private getStateByKey(eCode: string, swatchColor: string, namesSet: boolean): {STATE_KEY: StateKey<string>, stateData:string} {
		// Get unique key
		const STATE_KEY = makeStateKey<string>(
			`${this.MEDIA_SET_STATE_KEY_PREFIX}-${eCode}-${swatchColor}${namesSet ? '-namesSet' : ''}`
		);

		const stateData = this.state.get(STATE_KEY, null);

		// Attempt to get the state by key
		return { STATE_KEY, stateData };
	}

	private createHttpOptions(): HttpOptions {
		// Query String Parameters
		const httpParams = new HttpParams()
			.set('req', 'set,json,UTF-8')
			.set('labelkey', 'label')
			.set('handler', 'customScene7Handler');

		// HTTP Options
		return {
			params: httpParams,
			responseType: 'text' as const
		};
	}

	private requestError(error: SyntaxError): Error {
		return new Error(
			`${this.MEDIA_SET_PARSE_ERROR_PREFIX}${JSON.stringify(error, [
				'message',
				'arguments',
				'type',
				'name'
			])}`
		);
	}

	private getRequestUrl(params: RequestUrlParams): string {
		const { baseUrl, imageDirectory } = this.environment.thirdParty.scene7.baseConfig;
		const { cdnName, eCode, swatchColorPrefix, swatchColor, mediaSetSuffix } = params;

		return `${baseUrl}${imageDirectory}${cdnName}${eCode}${swatchColorPrefix}${swatchColor}${mediaSetSuffix}`;
	}

	private createMediaSetResult(response: any): Scene7MediaSetResponse {
		// List of unwanted text patterns to be removed
		const unwantedPatterns = [
			'/*jsonp*/',
			's7jsonError(',
			'customScene7Handler(',
			', "");',
			',"");'
		];

		// Remove unwanted text patterns and trim the response
		let formattedResponse = response;
		for (const pattern of unwantedPatterns) {
			formattedResponse = formattedResponse.replace(pattern, '');
		}

		formattedResponse.trim();

		// Parse the JSON string and return the parsed media set
		return JSON.parse(formattedResponse).set;
	}

	private resolveCdnName(): string {
		// Fix for GG NP0
		let imageCdn = this.environment.thirdParty.scene7.baseConfig.cdn;

		if (
			imageCdn === 'GolfGalaxyTest/' &&
			window.location.hostname.includes(`prod-${this.chain.seoIdentifier}`)
		) {
			imageCdn = 'GolfGalaxy/';
		}
		return imageCdn;
	}

	private getNamesAndTypesList(imagesSet: Scene7MediaSetResponse): Scene7MediaSetItemMin[] {
		const items = Array.isArray(imagesSet.item) ? imagesSet.item : [imagesSet.item];
    	return items.map(listItem => {
			const isImage =  imagesSet.type === 'img_set' ? true : !listItem.type ;
			const imageName = (listItem.i?.n || listItem.s.n).split('/').pop();
			const mediaSet: Scene7MediaSetItemMin = { imageName, isImage };
			if(listItem.type === 'video_set') {
				if (Array.isArray(listItem.set?.item)) {
					mediaSet.videoSrcs = listItem.set.item.map((setItem) => setItem.i.n);
				} else {
					mediaSet.videoSrcs = [listItem.set.item.i.n];
				}
			} else if (listItem.type === 'video'){
				mediaSet.videoSrcs = [imageName];
			}
			return mediaSet;
		});
	}

	private getProductImageData(
		params:RequestUrlParams,
		retries: number,
		getNames = false): Observable<any> {
		const {eCode, swatchColor, swatchColorPrefix, mediaSetSuffix} = params;
		const {STATE_KEY, stateData} = this.getStateByKey(eCode, swatchColor, getNames);

		// Check to see if state exists
		if(stateData) return of(stateData);

		const imageCdn = this.resolveCdnName();
		const httpOptions = this.createHttpOptions();
		const url = this.getRequestUrl({cdnName: imageCdn, eCode, swatchColorPrefix, swatchColor, mediaSetSuffix});

		// Make the HTTP Request
		return this.http
			.get(
				url,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					// Return Array
					let returnMediaSet: any = {};
					const imagesSet = this.createMediaSetResult(response);
					returnMediaSet = getNames ? this.getNamesAndTypesList(imagesSet) : imagesSet;
					// Set the state
					this.state.set(STATE_KEY, returnMediaSet);
					// Return the result
					return returnMediaSet;
				}),
				retry(retries),
				catchError((error) => {
					throw this.requestError(error);
				})
			);
	}

	/**
	 * @description
	 * Gets an image set from Scene7 for a given product and supplied arguments
	 */
	getProductImageSet$(
		eCode: string,
		swatchColor: string,
		swatchColorPrefix: string,
		mediaSetSuffix: string,
		retries = 0
	): Observable<Scene7MediaSetResponse> {
		return this.getProductImageData({eCode, swatchColor, swatchColorPrefix, mediaSetSuffix}, retries);
	}

	/**
	 * @description
	 * Gets image set names from Scene7 for a given product and supplied arguments
	 */
	getProductImageSetNames$(
		eCode: string,
		swatchColor: string,
		swatchColorPrefix: string,
		mediaSetSuffix: string,
		retries = 0
	): Observable<Scene7MediaSetItemMin[]> {
		return this.getProductImageData({eCode, swatchColor, swatchColorPrefix, mediaSetSuffix}, retries, true);
	}

	/**
	 * @description
	 * Gets an image collection from image manager for a given product and supplied arguments
	 */
	getImageCollection$(collectionId: string, retries = 0) {
		return this.http
			.get(this.environment.thirdParty.images.imageCollections.url + collectionId)
			.pipe(
				map((collectionData: any) => {
					return collectionData;
				}),
				retry(retries),
				catchError(Scene7Dao.handleError)
			);
	}
}
